import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import API_BASE_URL from "../services/apiClient";

const categories = [
  { name: "Food", icon: "🍴", color: "bg-orange-50 text-orange-600 border-orange-100" },
  { name: "Transport", icon: "🚗", color: "bg-blue-50 text-blue-600 border-blue-100" },
  { name: "Hotel", icon: "🏨", color: "bg-purple-50 text-purple-600 border-purple-100" },
  { name: "Shopping", icon: "🛍️", color: "bg-pink-50 text-pink-600 border-pink-100" },
  { name: "Activities", icon: "🎭", color: "bg-emerald-50 text-emerald-600 border-emerald-100" },
  { name: "Other", icon: "✨", color: "bg-gray-50 text-gray-600 border-gray-100" },
];

const ExpensesPage = () => {
  const { travelKeyToken } = useParams();
  const navigate = useNavigate();

  const [expenses, setExpenses] = useState([]);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);

  const emptyExpense = {
    title: "",
    amount: "",
    category: "Food",
    date: new Date().toISOString().split('T')[0],
    paidBy:"",
  };

  const [newExpense, setNewExpense] = useState(emptyExpense);

  /* ================= FETCH ================= */
  useEffect(() => {
    const fetchExpenses = async () => {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const res = await fetch(`${API_BASE_URL}/api/trips/${travelKeyToken}/expenses`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          const data = await res.json();
          setExpenses(data);
        }
      } catch (err) {
        console.error("Fetch expenses error:", err);
      } finally {
        setInitialLoaded(true);
        setLoading(false);
      }
    };

    fetchExpenses();
  }, [travelKeyToken, navigate]);

  /* ================= AUTO SAVE ================= */
  useEffect(() => {
    if (!initialLoaded) return;

    const saveExpenses = async () => {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      try {
        await fetch(`${API_BASE_URL}/api/trips/${travelKeyToken}/expenses`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ expenses }),
        });
      } catch (err) {
        console.error("Save expenses error:", err);
      }
    };

    const timer = setTimeout(saveExpenses, 1000);
    return () => clearTimeout(timer);
  }, [expenses, initialLoaded, travelKeyToken]);

  /* ================= ACTIONS ================= */
  const handleAddExpense = () => {
    if (!newExpense.title || !newExpense.amount ||!newExpense.paidBy) return;
    setExpenses([{ ...newExpense, id: Date.now() }, ...expenses]);
    setNewExpense(emptyExpense);
    setShowAddForm(false);
  };

  const removeExpense = (id) => {
    setExpenses(expenses.filter((e) => e.id !== id));
  };

  const total = expenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);

  const getCategoryTheme = (catName) => {
    return categories.find(c => c.name === catName) || categories[5];
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-20">
        <div className="w-10 h-10 border-4 border-black border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-xs font-black uppercase tracking-widest text-gray-400">Loading Budget...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <h1 className="text-4xl font-black tracking-tight text-gray-900 mb-2">Trip Budget</h1>
          <p className="text-gray-500 font-medium">Track your spending and stay within your travel budget.</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-black text-white px-8 py-4 rounded-full font-bold shadow-2xl shadow-gray-200 hover:scale-105 active:scale-95 transition-all flex items-center gap-2 w-fit"
        >
          <span className="text-xl">+</span> Add Expense
        </button>
      </div>

      {/* Summary Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-indigo-600 rounded-[32px] p-10 mb-12 text-white shadow-2xl shadow-indigo-100 relative overflow-hidden group"
      >
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500 rounded-full -mr-20 -mt-20 opacity-50 group-hover:scale-110 transition-transform duration-1000"></div>
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <p className="text-xs font-black uppercase tracking-widest opacity-70 mb-2">Total Travel Spending</p>
            <h2 className="text-5xl md:text-6xl font-black tracking-tighter">₹ {total.toLocaleString('en-IN')}</h2>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/10">
            <p className="text-[10px] font-black uppercase tracking-widest opacity-70 mb-1">Expenses Logged</p>
            <p className="text-2xl font-black">{expenses.length} Items</p>
          </div>
        </div>
      </motion.div>

      {/* Add Expense Form Card */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl mb-12"
          >
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-xl font-black">New Expense</h3>
              <button
                onClick={() => setShowAddForm(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-50 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-2">Title</label>
                <input
                  placeholder="e.g. Dinner at Beach, Souvenirs"
                  value={newExpense.title}
                  onChange={(e) => setNewExpense({ ...newExpense, title: e.target.value })}
                  className="w-full bg-gray-50 border-none rounded-2xl px-5 py-4 focus:ring-2 focus:ring-black transition-all font-bold placeholder:text-gray-300"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-2">Amount (₹)</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={newExpense.amount}
                  onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                  className="w-full bg-gray-50 border-none rounded-2xl px-5 py-4 focus:ring-2 focus:ring-black transition-all font-bold placeholder:text-gray-300"
                />
              </div>


              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-2">Category</label>
                <select
                  value={newExpense.category}
                  onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })}
                  className="w-full bg-gray-50 border-none rounded-2xl px-5 py-4 focus:ring-2 focus:ring-black transition-all font-bold"
                >
                  {categories.map((cat) => (
                    <option key={cat.name} value={cat.name}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-2">
                  Paid By
                </label>
                <input
                  placeholder="e.g. Praseed, Rahul"
                  value={newExpense.paidBy}
                  onChange={(e) =>
                    setNewExpense({ ...newExpense, paidBy: e.target.value })
                  }
                  className="w-full bg-gray-50 border-none rounded-2xl px-5 py-4 focus:ring-2 focus:ring-black transition-all font-bold placeholder:text-gray-300"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-2">Date</label>
                <input
                  type="date"
                  value={newExpense.date}
                  onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })}
                  className="w-full bg-gray-50 border-none rounded-2xl px-5 py-4 focus:ring-2 focus:ring-black transition-all font-bold"
                />
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <button
                onClick={() => setShowAddForm(false)}
                className="px-8 py-4 rounded-full font-bold text-gray-400 hover:text-black transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleAddExpense}
                className="bg-black text-white px-10 py-4 rounded-full font-bold shadow-xl active:scale-95 transition-all"
              >
                Add Record
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Expenses List */}
      <div className="space-y-4">
        {expenses.length === 0 ? (
          <div className="bg-white rounded-[32px] p-20 border-2 border-dashed border-gray-100 flex flex-col items-center text-center">
            <div className="text-6xl mb-6 opacity-20">💰</div>
            <h3 className="text-2xl font-black text-gray-900 mb-2">No expenses recorded</h3>
            <p className="text-gray-400 font-medium mb-8 max-w-sm">
              Keep an eye on your travel funds. Add your first expense to get started.
            </p>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-gray-100 text-gray-900 px-6 py-3 rounded-full font-bold hover:bg-gray-200 transition-all"
            >
              + Log Expense
            </button>
          </div>
        ) : (
          expenses.map((expense) => {
            const theme = getCategoryTheme(expense.category);
            return (
              <motion.div
                layout
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                key={expense.id}
                className="group bg-white rounded-3xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-all flex items-center justify-between gap-4"
              >
                <div className="flex items-center gap-5">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl border ${theme.color} transition-transform group-hover:rotate-6`}>
                    {theme.icon}
                  </div>
                  <div>
                    <h4 className="text-lg font-black text-gray-900 leading-tight mb-1">{expense.title}</h4>
                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                      Paid by {expense.paidBy || "Unknown"}
                    </p>
                    <div className="flex items-center gap-3">
                      <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded border ${theme.color}`}>
                        {expense.category}
                      </span>
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                        {expense.date ? new Date(expense.date).toLocaleDateString([], { month: 'short', day: 'numeric' }) : 'Today'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-xl font-black text-gray-900 tracking-tight">₹ {Number(expense.amount).toLocaleString('en-IN')}</p>
                  </div>
                  <button
                    onClick={() => removeExpense(expense.id)}
                    className="p-3 text-gray-200 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ExpensesPage;